import { useCallback } from "react";

import { TabContext, TabPanel } from "@mui/lab";
import { Box, Stack, Tab, Tabs } from "@mui/material";
import { Typography } from "components";
import { gray } from "configs/colors";
import i18n from "translations/i18n";
import { rem } from "utils";

interface TabProps {
  value: string;
  listTab: {
    label?: string | React.ReactNode;
    labelI18n?: string;
    value: string;
    quantity?: number;
    component?: React.ReactNode;
    disabled?: boolean;
  }[];
  onChange: (e: string) => void;
  keepTabpanel?: boolean;
  sx?: React.ComponentProps<typeof Box>["sx"];
}

const TLNTab: React.FC<TabProps> = ({
  value,
  listTab,
  sx,
  keepTabpanel = true,
  onChange,
}) => {
  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    onChange(newValue);
  };
  const renderBadge = useCallback((label: string) => {
    return (
      <Stack
        border={`1px solid ${gray[200]}`}
        bgcolor={gray[50]}
        borderRadius="16px"
        padding={`0 ${rem(8)}`}
        className="badge-employee"
      >
        <Typography fontWeight={500} type="TextSM">
          {label}
        </Typography>
      </Stack>
    );
  }, []);
  return (
    <Box
      sx={{
        "& .MuiTab-root": {
          fontSize: "16px",
          fontWeight: "600",
          textTransform: "none",
        },
        "& .MuiTabPanel-root": {
          padding: 0,
        },
        ...sx,
      }}
    >
      <TabContext value={value}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }} mb={rem(16)}>
          <Tabs
            value={value}
            onChange={handleChange}
            scrollButtons="auto"
            variant="scrollable"
          >
            {listTab.map((tab) => (
              <Tab
                key={tab.value}
                label={
                  <Stack direction="row" columnGap={rem(8)}>
                    {tab.labelI18n ? i18n.t(tab.labelI18n) : tab.label}{" "}
                    {tab.quantity ? renderBadge(tab.quantity.toString()) : null}
                  </Stack>
                }
                value={tab.value}
                disabled={tab?.disabled}
              />
            ))}
          </Tabs>
        </Box>
        {keepTabpanel ? (
          <TabPanel value={value}>
            {listTab.map((tab) => (
              <Box
                key={tab.value}
                sx={{ display: tab.value === value ? "block" : "none" }}
              >
                {tab.component}
              </Box>
            ))}
          </TabPanel>
        ) : (
          <>
            {listTab.map((tab) => (
              <TabPanel key={tab.value} value={tab.value}>
                {tab.component}
              </TabPanel>
            ))}
          </>
        )}
      </TabContext>
    </Box>
  );
};

export default {
  Context: TLNTab,
  Panel: TabPanel,
};
